import { Boxes, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { AdminRunMicrovm, AdminRunTrace, AdminRunTraceSpan } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { lifecycleLabel, lifecycleTone } from '@/lib/microvm';
import { formatClockTime, formatDurationMs, humanizeKey } from '@/lib/runTelemetry';
import { formatTick, layoutTrace, topLevelShare, traceExtentMs, traceTicks } from '@/lib/runTrace';
import { cn } from '@/lib/utils';

/** Bar colour by the top-level span a row belongs to, so a phase reads the same at every depth. */
const ROOT_COLORS: Record<string, string> = {
  queue: 'bg-muted-foreground/40',
  claim: 'bg-sky-500',
  'agent-boot': 'bg-violet-400',
  agent: 'bg-emerald-500',
  delivery: 'bg-amber-500',
  failed: 'bg-destructive',
};

function rootOf(span: AdminRunTraceSpan, byId: Map<string, AdminRunTraceSpan>): string {
  let current = span;
  while (current.parentId && byId.has(current.parentId)) current = byId.get(current.parentId)!;
  return current.id;
}

function attrsTitle(span: AdminRunTraceSpan, durationMs: number): string {
  const lines = [`${span.label}: ${formatDurationMs(durationMs)}${span.status === 'open' ? ' (still running)' : ''}`];
  for (const [key, value] of Object.entries(span.attrs ?? {})) lines.push(`${key}: ${String(value)}`);
  return lines.join('\n');
}

export function RunTraceWaterfall({ trace, microvm }: { trace: AdminRunTrace | null | undefined; microvm?: AdminRunMicrovm | null }) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  const [selectedId, setSelectedId] = useState<string | undefined>();

  if (!trace || trace.spans.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border px-4 py-5 text-sm text-muted-foreground">
        No trace recorded for this run.
      </p>
    );
  }

  const nowMs = Math.max(0, Date.now() - Date.parse(trace.origin.at));
  const extent = traceExtentMs(trace, nowMs);
  const rows = layoutTrace(trace, nowMs, collapsed);
  const ticks = traceTicks(extent);
  const byId = new Map(trace.spans.map((span) => [span.id, span]));
  const selected = selectedId ? rows.find((row) => row.span.id === selectedId) : undefined;
  const share = topLevelShare(trace, nowMs);
  const select = (id: string) => setSelectedId((current) => (current === id ? undefined : id));
  const toggle = (id: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>
          {trace.inProgress ? 'Running for' : 'Total'}{' '}
          <span className="font-mono text-foreground tabular-nums">{formatDurationMs(extent)}</span> from {humanizeKey(trace.origin.key)} at{' '}
          {formatClockTime(trace.origin.at)}
        </span>
        {trace.inProgress ? <StatusPill tone="active">Live</StatusPill> : null}
        {microvm?.microvmId ? (
          <Link
            to="/platform"
            className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 font-mono text-[11px] text-foreground hover:bg-muted"
            title="Open the MicroVM fleet"
          >
            <Boxes className="h-3 w-3" aria-hidden="true" />
            {microvm.microvmId}
            {microvm.imageVersion ? <span className="text-muted-foreground">v{microvm.imageVersion}</span> : null}
          </Link>
        ) : null}
      </div>

      <div className="flex h-2 overflow-hidden rounded-full bg-muted" aria-hidden="true">
        {share.map((part) => (
          <span key={part.id} className={cn('h-full', ROOT_COLORS[part.id] ?? 'bg-muted-foreground/50')} style={{ width: `${part.pct}%` }} title={`${part.label}: ${formatDurationMs(part.durationMs)}`} />
        ))}
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[44rem]">
          <div className="grid grid-cols-[18rem_1fr] border-b border-border pb-1 text-[10px] text-muted-foreground">
            <span className="pl-1">Step</span>
            <div className="relative h-4">
              {ticks.map((tick) => (
                <span key={tick} className="absolute -translate-x-1/2 tabular-nums" style={{ left: `${(tick / extent) * 100}%` }}>
                  {formatTick(tick)}
                </span>
              ))}
            </div>
          </div>
          <ol>
            {rows.map((row) => {
              const { span } = row;
              const root = rootOf(span, byId);
              const isCollapsed = collapsed.has(span.id);
              return (
                <li
                  key={span.id}
                  className={cn('grid grid-cols-[18rem_1fr] items-center hover:bg-muted/50', selectedId === span.id && 'bg-muted/60')}
                  title={attrsTitle(span, row.durationMs)}
                >
                  <span className="flex min-w-0 items-center gap-1 py-1 pr-2" style={{ paddingLeft: `${row.depth * 16 + 4}px` }}>
                    {row.hasChildren ? (
                      <button
                        type="button"
                        aria-label={isCollapsed ? `Expand ${span.label}` : `Collapse ${span.label}`}
                        aria-expanded={!isCollapsed}
                        className="rounded p-0.5 text-muted-foreground hover:text-foreground"
                        onClick={() => toggle(span.id)}
                      >
                        {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </button>
                    ) : (
                      <span className="w-[18px] shrink-0" />
                    )}
                    <button
                      type="button"
                      onClick={() => select(span.id)}
                      className={cn('min-w-0 flex-1 truncate text-left text-xs', row.depth === 0 ? 'font-medium text-foreground' : 'text-muted-foreground', span.status === 'error' && 'text-destructive')}
                    >
                      {span.label}
                    </button>
                    <span className="shrink-0 pl-2 font-mono text-[11px] tabular-nums text-muted-foreground">
                      {formatDurationMs(row.durationMs)}
                      {span.status === 'open' ? '…' : ''}
                    </span>
                  </span>
                  <button type="button" onClick={() => select(span.id)} aria-label={`Details for ${span.label}`} className="relative block h-5 w-full">
                    {ticks.map((tick) => (
                      <span key={tick} className="absolute inset-y-0 w-px bg-border/60" style={{ left: `${(tick / extent) * 100}%` }} aria-hidden="true" />
                    ))}
                    <span
                      className={cn(
                        'absolute top-1 h-3 rounded-sm',
                        span.status === 'error' ? 'bg-destructive' : ROOT_COLORS[root] ?? 'bg-muted-foreground/50',
                        row.depth > 0 && span.status !== 'error' && 'opacity-70',
                        span.status === 'open' && 'animate-pulse',
                      )}
                      style={{ left: `${row.leftPct}%`, width: `max(2px, ${row.widthPct}%)` }}
                    />
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      {selected ? (
        <div className="rounded-lg border border-border px-4 py-3 text-xs">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-sm font-medium text-foreground">{selected.span.label}</span>
            <span className="font-mono tabular-nums text-muted-foreground">
              +{formatDurationMs(selected.span.startMs)} → {selected.span.endMs === undefined ? 'still running' : `+${formatDurationMs(selected.span.endMs)}`}
            </span>
            <span className="font-mono tabular-nums text-foreground">{formatDurationMs(selected.durationMs)}</span>
          </div>
          {selected.span.attrs && Object.keys(selected.span.attrs).length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {Object.entries(selected.span.attrs).map(([key, value]) => (
                <span key={key} className="rounded border border-border px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                  <span className="opacity-70">{key}</span> {String(value)}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {microvm && microvm.lifecycle.length > 0 ? (
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-muted-foreground">This workspace&apos;s MicroVM around the run</div>
          <ol className="space-y-1">
            {microvm.lifecycle.map((event, index) => (
              <li key={`${event.at}:${event.type}:${index}`} className="flex flex-wrap items-center gap-2 text-xs">
                <span className="w-24 font-mono tabular-nums text-muted-foreground">{formatClockTime(event.at)}</span>
                <StatusPill tone={lifecycleTone(event.type)}>{lifecycleLabel(event.type)}</StatusPill>
                <span className="font-mono">{event.microvmId}</span>
                {event.durationMs !== undefined ? <span className="tabular-nums text-muted-foreground">{formatDurationMs(event.durationMs)}</span> : null}
                {event.reason ? <span className="text-muted-foreground">{humanizeKey(event.reason)}</span> : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}
